import {
  type CustomColormapLight,
  useDeleteCustomColormap,
  useGetCustomColormap,
  useGetCustomColormaps,
  useUploadCustomColormap,
} from "@/api/hooks/customColormaps";
import { Select } from "@/components/atoms/Select/Select";
import { OptionsMenu, type OptionsMenuItem } from "@/components/molecules/OptionsMenu/OptionsMenu";
import { TabButton } from "@/components/organisms/LocationSearch/TabButton";
import type { LayerUnion } from "@/layers";
import { classNames } from "@/utility/jsx";
import { isColorMap } from "@/utility/typeGuard";
import { unsafeGetColormapByName } from "@/utility/unsafeGetColormapByName";
import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { ColorMap, colormaps } from "@mm/api.meteomatics.com";
import type { ColormapCreate } from "@mm/metx-workbench.meteomatics.com";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CustomColormapsEditor } from "./CustomColormapsEditor";
import { CustomColormapsLegendPreview } from "./CustomColormapsLegendPreview";
import "./CustomColormapsManager.scss";
import { CustomColormapsUploader, type CustomColormapsUploaderHandle } from "./CustomColormapsUploader";
import { useBaseColormap } from "./useBaseColormap";

/**
 * Fallback colormap used while loading or when no source colormap is selected.
 */
const DEFAULT_COLORMAP: ColormapCreate = {
  type: "continuous",
  label: "Fallback Colormap",
  stops: [
    [0, "#00000000"],
    [1, "#f76060"],
    [10, "#e40808"],
    [15, "#7c0707"],
  ],
};

/**
 * Converts a label into a filesystem-safe filename segment.
 */
const sanitizeFilename = (value: string) => {
  const sanitized = value
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, "_")
    .replace(/^_+|_+$/g, "");

  return sanitized || "colormap";
};

/**
 * Triggers a JSON download in the browser.
 */
const downloadJsonFile = (filename: string, data: unknown) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  link.style.display = "none";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

type CustomColormapsManagerProps = {
  /**
   * Current layer colormap identifier (can be a default colormap name or a custom colormap id).
   */
  initialColormapId: string;
  /**
   * Weather parameter used to resolve fallback/default colormaps.
   */
  parameter: string;
  /**
   * Persists colormap selection changes to the owning layer.
   */
  onSave: (propsValue: Partial<LayerUnion>) => void;
  /**
   * Closes the manager modal/dialog.
   */
  onClose: () => void;
};

/**
 * Modal content for creating, editing, importing, exporting, and deleting custom colormaps.
 *
 * The component supports two modes:
 * - `create`: start from a template (default or existing custom colormap)
 * - `edit`: modify an existing custom colormap
 */
export function CustomColormapsManager({ initialColormapId, parameter, onSave, onClose }: CustomColormapsManagerProps) {
  const uploaderRef = useRef<CustomColormapsUploaderHandle>(null);
  const { i18n } = useLingui();
  const [selectedColormapId, setSelectedColormapId] = useState<string | null>(initialColormapId);
  const [colormap, setColormap] = useState<ColormapCreate>(DEFAULT_COLORMAP);
  const [activeTab, setActiveTab] = useState<"create" | "edit">(
    selectedColormapId && !isColorMap(selectedColormapId) ? "edit" : "create",
  );
  const defaultColormapNames: ColorMap[] = colormaps.filter((map) => map !== "satellite");
  const { list: customColormapList } = useGetCustomColormaps();
  const hasCustomColormaps = (customColormapList?.length ?? 0) > 0;

  const selectedCustomColormapId = selectedColormapId && !isColorMap(selectedColormapId) ? selectedColormapId : null;
  const selectedBaseColormapName = selectedColormapId && isColorMap(selectedColormapId) ? selectedColormapId : null;
  const selectedCustomColormap = customColormapList?.find((map) => map.id === selectedCustomColormapId);
  const baseColormapId =
    selectedBaseColormapName ?? (!selectedCustomColormapId && isColorMap(initialColormapId) ? initialColormapId : "");

  const { mutateAsync: deleteColormap } = useDeleteCustomColormap();
  const { mutateAsync: uploadCustomColormap, isLoading: isUploadingColormap } = useUploadCustomColormap();
  const { colormap: fetchedCustomColormap } = useGetCustomColormap(
    selectedCustomColormapId ?? undefined,
    Boolean(selectedCustomColormapId),
    {
      refetchOnMount: "always",
    },
  );

  const { data: fetchedBaseColormap } = useBaseColormap(baseColormapId, parameter, {
    enabled: Boolean(baseColormapId),
    refetchOnMount: "always",
    onError: (error) => {
      console.error("Failed to load colormap steps:", error);
    },
  });

  useEffect(() => {
    if (!fetchedCustomColormap) {
      return;
    }

    const nextColormap: ColormapCreate = {
      type: fetchedCustomColormap.type === "segmented" ? "segmented" : "continuous",
      label: fetchedCustomColormap.label,
      stops: fetchedCustomColormap.stops,
    };

    setSelectedColormapId(fetchedCustomColormap.id);
    setColormap(nextColormap);
  }, [fetchedCustomColormap]);

  useEffect(() => {
    if (!fetchedBaseColormap || selectedCustomColormapId) {
      return;
    }

    setColormap(fetchedBaseColormap);
  }, [fetchedBaseColormap, selectedCustomColormapId]);

  const handleDelete = useCallback(async () => {
    if (!selectedCustomColormapId) {
      return;
    }

    const list = customColormapList ?? [];
    const selectedIndex = list.findIndex((map) => map.id === selectedCustomColormapId);
    const nextId =
      list.length > 1 ? list[selectedIndex + 1]?.id ?? list[selectedIndex - 1]?.id ?? list[0]?.id ?? null : null;

    try {
      await deleteColormap(selectedCustomColormapId);
      if (nextId) {
        setSelectedColormapId(nextId);
        onSave({ color_map: nextId });
        setActiveTab("edit");
      } else {
        const defaultColormap = unsafeGetColormapByName("", parameter);
        setSelectedColormapId(null);
        onSave({ color_map: defaultColormap });
        setActiveTab("create");
      }
    } catch (error) {
      console.error("Failed to delete colormap:", error);
    }
  }, [selectedCustomColormapId, customColormapList, deleteColormap, onSave, parameter]);

  const handleSave = (colormapId: string) => {
    onSave({ color_map: colormapId });
    onClose(); //closes modal
  };

  const handleExport = useCallback(() => {
    if (!selectedCustomColormapId) {
      return;
    }

    const filenameBase = sanitizeFilename(colormap.label || selectedCustomColormap?.label || selectedCustomColormapId);
    downloadJsonFile(`${filenameBase}.json`, colormap);
  }, [colormap, selectedCustomColormap?.label, selectedCustomColormapId]);

  const menuItems = useMemo<OptionsMenuItem[]>(
    () => [
      {
        id: "import_colormap",
        label: t`Import`,
        iconName: "upload",
        onSelect: () => {
          uploaderRef.current?.openFilePicker();
        },
        disabled: isUploadingColormap,
      },
      {
        id: "export_colormap",
        label: t`Export`,
        iconName: "download",
        onSelect: handleExport,
        disabled: !selectedCustomColormapId || !fetchedCustomColormap,
      },
    ],
    [fetchedCustomColormap, handleExport, isUploadingColormap, selectedCustomColormapId, i18n.locale],
  );

  return (
    <div className={"manage-container"}>
      <div className={"header-cc"}>
        <div className="title">
          <Trans>Custom Colormap Manager</Trans>
        </div>
        <div className={"tab-switch"} role="tablist" aria-label={t`Custom colormap mode`}>
          <TabButton
            active={activeTab === "create"}
            label={<Trans>Create</Trans>}
            onClick={() => {
              setActiveTab("create");
              setSelectedColormapId(null);
              uploaderRef.current?.clearStatus();
            }}
            data-testid="location-tab"
          />
          <TabButton
            active={activeTab === "edit"}
            label={<Trans>Edit</Trans>}
            onClick={() => {
              if (!hasCustomColormaps) {
                return;
              }
              setActiveTab("edit");
              setSelectedColormapId(customColormapList?.[0]?.id ?? null);
            }}
            disabled={!hasCustomColormaps}
            data-testid="station-tab"
          />
        </div>

        {activeTab === "edit" ? (
          <div className={"header-row"}>
            <Select
              block
              label={t`Select custom colormap`}
              value={selectedColormapId ?? ""}
              onChange={(event) => setSelectedColormapId(event.target.value || null)} // fetch custom color map and setColormap
            >
              {(customColormapList ?? []).map((map: CustomColormapLight) => (
                <option key={map.id} value={map.id}>
                  {map.label}
                </option>
              ))}
            </Select>
            <OptionsMenu items={menuItems} className={classNames("options-button", "more-button")} />
            {selectedColormapId && (
              <button className={"deleteButton"} type="button" onClick={handleDelete} aria-label={t`Delete step`}>
                <span className="material-icons">delete</span>
              </button>
            )}
          </div>
        ) : (
          <div className={"header-row"}>
            <Select
              block
              label={t`Create from template`}
              value={selectedColormapId ?? ""}
              onChange={(event) => setSelectedColormapId(event.target.value || null)}
            >
              <option disabled={true}>
                <Trans>-- Custom Colormaps --</Trans>
              </option>
              {(customColormapList ?? []).map((map: CustomColormapLight) => (
                <option key={map.id} value={map.id}>
                  {map.label}
                </option>
              ))}
              <option disabled={true}>
                <Trans>-- Available Colormaps --</Trans>
              </option>
              {defaultColormapNames?.map((map) => (
                <option key={map} value={map}>
                  {ColorMap[map]}
                </option>
              ))}
            </Select>
            <OptionsMenu items={[menuItems[0]]} className={classNames("options-button", "more-button")} />
          </div>
        )}
        <CustomColormapsUploader
          ref={uploaderRef}
          onImportColormap={uploadCustomColormap}
          onImportSuccess={(uploadedColormapId) => {
            setActiveTab("edit");
            setSelectedColormapId(uploadedColormapId);
          }}
        />
      </div>
      <div className={"manage-edit-section"}>
        <CustomColormapsEditor
          selectedColormapId={selectedColormapId ?? undefined}
          colormap={colormap}
          setColormap={setColormap}
          onSave={handleSave}
          activeTab={activeTab}
        />
        <CustomColormapsLegendPreview colormap={colormap} />
      </div>
    </div>
  );
}
